(() => {
  if (window.HpDependentFacets) return;

  const marker = 'data-hp-facet-hidden';
  const valueSelector = 'input[type="checkbox"][name^="filter."], input[type="radio"][name^="filter."]';
  const itemSelector = '.hp-facets-filter-show-more__item, .facets-filter-show-more__item';
  const listSelector = '.hp-facets-filter-show-more, facets-filter-show-more';

  function setHidden(element, hidden) {
    element.toggleAttribute(marker, hidden);
  }

  function updateShowMore(scope) {
    scope.querySelectorAll(listSelector).forEach((list) => {
      const items = Array.from(list.querySelectorAll(itemSelector)).filter((item) => !item.hasAttribute(marker));
      const limit = Number(list.dataset.limit) || 10;
      items.forEach((item, index) => item.classList.toggle('is-collapsed', index >= limit));
      if (items.slice(limit).some((item) => item.querySelector('input:checked'))) list.classList.add('is-expanded');
      const expanded = list.classList.contains('is-expanded');
      const toggle = list.querySelector('[data-facets-show-more], [data-facets-filter-toggle]');
      if (!toggle) return;
      setHidden(toggle, items.length <= limit);
      toggle.setAttribute('aria-expanded', String(expanded));
      list.querySelectorAll('[data-show-more], .hp-facets-filter-show-more__label--more').forEach((label) => { label.hidden = expanded; });
      list.querySelectorAll('[data-show-less], .hp-facets-filter-show-more__label--less').forEach((label) => { label.hidden = !expanded; });
    });
  }

  class DependentFacets extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.initial) return;
        this.scope = this.closest('facets-form, xo-modal');
        if (!this.scope) return;
        try {
          this.initial = JSON.parse(this.dataset.filters);
        } catch {
          return;
        }
        this.version = 0;
        this.status = this.querySelector('[role="status"]');
        this.apply(this.initial, true);
      });
    }

    disconnectedCallback() {
      this.cancel();
    }

    setStatus(message, busy = false) {
      if (this.status) {
        this.status.textContent = message;
        this.status.hidden = !message;
      }
      if (this.scope) {
        if (busy) this.scope.setAttribute('aria-busy', 'true');
        else this.scope.removeAttribute('aria-busy');
      }
    }

    cancel() {
      this.version = (this.version || 0) + 1;
      clearTimeout(this.timer);
      this.controller?.abort();
      this.setStatus('');
    }

    reset() {
      this.cancel();
      if (this.initial) this.apply(this.initial);
    }

    apply(filters, initial = false) {
      const values = new Map();
      filters.forEach((filter) => {
        if (filter.type === 'price_range') return;
        values.set(filter.name, new Map(filter.values.map((value) => [String(value.value), value])));
      });
      const groups = new Map();
      this.scope.querySelectorAll(valueSelector).forEach((input) => {
        const value = values.get(input.name)?.get(input.value);
        const visible = Boolean(input.checked || (initial && value?.active) || Number(value?.count) > 0);
        const row = input.closest(itemSelector) || input.closest('xo-filters-field, .checkbox-control');
        if (row) setHidden(row, !visible);
        else {
          setHidden(input, !visible);
          Array.from(input.labels || []).forEach((label) => setHidden(label, !visible));
        }
        const field = input.closest('xo-filters-field');
        field?.toggleAttribute('xo-disabled', !visible);
        const counter = field?.querySelector('xo-filters-count');
        if (counter) counter.textContent = ` (${Number(value?.count) || 0}) `;
        if (this.dataset.showCounts === 'true' && !field) {
          Array.from(input.labels || []).forEach((label) => {
            const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
            while (walker.nextNode()) {
              const node = walker.currentNode;
              if (/\(\d+\)\s*$/.test(node.textContent)) {
                node.textContent = node.textContent.replace(/\(\d+\)(\s*)$/, `(${Number(value?.count) || 0})$1`);
              }
            }
          });
        }
        const group = input.closest('.xo-collapse, accordion-disclosure');
        if (group) groups.set(group, (groups.get(group) || false) || visible);
      });
      groups.forEach((visible, group) => setHidden(group, !visible));
      updateShowMore(this.scope);
    }

    refresh(targetUrl) {
      this.cancel();
      if (!this.initial) return;
      const url = new URL(targetUrl, window.location.href);
      if (url.origin !== window.location.origin || !this.dataset.sectionId) return;
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.delete('page');
      const version = this.version;
      this.setStatus(window.themeVariables?.strings?.facetsUpdating || '', true);
      this.timer = setTimeout(async () => {
        const controller = new AbortController();
        this.controller = controller;
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Facets request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = Array.from(html.querySelectorAll('hp-dependent-facets')).find((element) => element.dataset.sectionId === this.dataset.sectionId);
          if (!payload) throw new Error('Facets response missing');
          const filters = JSON.parse(payload.dataset.filters);
          if (!Array.isArray(filters)) throw new Error('Invalid facets response');
          if (version !== this.version || !this.isConnected) return;
          this.apply(filters);
          this.setStatus('');
        } catch {
          if (version === this.version && this.isConnected) {
            this.setStatus(window.themeVariables?.strings?.facetsUpdateFailed || '');
          }
        } finally {
          clearTimeout(timeout);
        }
      }, 200);
    }
  }

  window.HpDependentFacets = {
    refresh(scope, url) {
      const element = scope?.querySelector('hp-dependent-facets');
      if (!element?.initial) return false;
      element.refresh(url);
      return true;
    },
    reset(scope) { scope?.querySelector('hp-dependent-facets')?.reset(); },
    cancel(scope) { scope?.querySelector('hp-dependent-facets')?.cancel(); },
  };
  customElements.define('hp-dependent-facets', DependentFacets);
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-facets-show-more]');
    if (!toggle?.closest('xo-modal')?.querySelector('hp-dependent-facets')) return;
    queueMicrotask(() => {
      toggle.setAttribute('aria-expanded', String(toggle.closest(listSelector).classList.contains('is-expanded')));
    });
  });
})();
